import { Path, UseFormSetError } from "react-hook-form";

/**
 * Validates that a field is not empty or only whitespace
 * @param value - The field value to validate
 * @param fieldName - The name of the field for error reporting
 * @param setError - React Hook Form's setError function
 * @param errorMessage - Custom error message (optional)
 * @returns true if validation failed, false if passed
 */
export const validateRequiredField = <T extends Record<string, unknown>>(
  value: null | string | undefined,
  fieldName: Path<T>,
  setError: UseFormSetError<T>,
  errorMessage?: string,
): boolean => {
  if (!value || value.trim().length === 0) {
    setError(fieldName, {
      message: errorMessage || `Please enter a ${String(fieldName)}`,
      type: "required",
    });
    return true; // validation failed
  }
  return false; // validation passed
};

/**
 * Validates multiple required fields and returns whether any validation failed
 * @param validations - Array of validation objects with field name, value, and optional error message
 * @param setError - React Hook Form's setError function
 * @returns true if any validation failed, false if all passed
 */
export const validateRequiredFields = <T extends Record<string, unknown>>(
  validations: Array<{
    errorMessage?: string;
    fieldName: Path<T>;
    value: null | string | undefined;
  }>,
  setError: UseFormSetError<T>,
): boolean => {
  let hasValidationError = false;

  for (const validation of validations) {
    if (
      validateRequiredField(
        validation.value,
        validation.fieldName,
        setError,
        validation.errorMessage,
      )
    ) {
      hasValidationError = true;
    }
  }

  return hasValidationError;
};
